import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, ComboBox, Group, Input, ListBox, ListBoxItem, Popover, Select as AriaSelect, SelectValue } from 'react-aria-components';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useT } from '../../../i18n/hooks';
import { FM } from './messages';
import { FieldFoot, FieldLabel, fieldClass } from './parts';
import type { Choice, SelectProps } from './types';
import './styles';

/** Пустая строка ключом коллекции быть не может: «не важно» получает служебный ключ на входе и теряет его на выходе. */
const EMPTY_KEY = '__empty__';
const toKey = (value: string) => (value === '' ? EMPTY_KEY : value);
const fromKey = (key: unknown) => (key === EMPTY_KEY ? '' : String(key));

/** Больше стольких пунктов список становится поиском: прокручивать длинный список глазами нельзя. */
const AUTO_SEARCH_OVER = 30;

/** `plain`: название обычным весом (список поиска), иначе жирным над подписью (закрытый список). */
function Option({ item, plain = false }: { item: Choice & { id: string }; plain?: boolean }) {
  return (
    <ListBoxItem id={item.id} className="ac-option" textValue={item.name} isDisabled={item.isDisabled}>
      <span>
        {plain ? <span>{item.name}</span> : <strong>{item.name}</strong>}
        {item.description ? <small>{item.description}</small> : null}
      </span>
      <Check size={15} className="ac-check" />
    </ListBoxItem>
  );
}

/**
 * Выбор из списка. До 30 пунктов — закрытый список (`Select`), больше или с
 * `searchable` — поле поиска (`ComboBox`). С `onSearch` поиск идёт на сервере:
 * встроенный фильтр выключен, `options` — ответ на запрос.
 */
export function Select<V extends string = string>(props: SelectProps<V>) {
  const searchable = props.searchable ?? (props.options.length > AUTO_SEARCH_OVER || props.onSearch !== undefined);
  return searchable ? <SearchSelect {...props} /> : <ListSelect {...props} />;
}

function ListSelect<V extends string>({ label, 'aria-label': ariaLabel, explain, description, errorMessage, isInvalid, isRequired, isDisabled, size = 'md', className, value, onChange, options, placeholder }: SelectProps<V>) {
  const t = useT();
  const items = useMemo(() => options.map((option) => ({ ...option, id: toKey(option.id) })), [options]);
  const hasEmptyOption = options.some((option) => option.id === '');
  return (
    <AriaSelect
      className={fieldClass(size, className)}
      selectedKey={value === '' && !hasEmptyOption ? null : toKey(value)}
      onSelectionChange={(key) => {
        if (key !== null) onChange(fromKey(key) as V | '');
      }}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isRequired={isRequired}
      validationBehavior="aria"
      placeholder={placeholder ?? t(FM.selectPlaceholder)}
      aria-label={label ? undefined : ariaLabel}
    >
      <FieldLabel label={label} ariaLabel={ariaLabel} explain={explain} isRequired={isRequired} />
      <Button className="ac-trigger">
        {/* В триггере — только текст выбранного, а не вся строка списка: иначе туда
            переезжают знак выбора и подпись «чем отличается». */}
        <SelectValue>{({ isPlaceholder, selectedText, defaultChildren }) => (isPlaceholder ? defaultChildren : selectedText)}</SelectValue>
        <ChevronDown size={15} />
      </Button>
      <FieldFoot description={description} errorMessage={errorMessage} isInvalid={isInvalid} />
      <Popover className="ac-popover fm-popover">
        <ListBox className="ac-list" items={items}>
          {(item) => <Option item={item} />}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}

function SearchSelect<V extends string>({ label, 'aria-label': ariaLabel, explain, description, errorMessage, isInvalid, isRequired, isDisabled, size = 'md', className, value, onChange, options, placeholder, emptyText, onSearch, searchDelay = 250, isLoading, valueLabel }: SelectProps<V>) {
  const t = useT();
  const remote = onSearch !== undefined;

  // На сервере выбранного пункта может не быть в текущем ответе: запоминаем выбранный,
  // иначе поле очистится, как только запрос сузит список.
  const [picked, setPicked] = useState<Choice<V> | null>(null);
  const items = useMemo(() => {
    const list: Array<Choice & { id: string }> = options.map((option) => ({ ...option, id: toKey(option.id) }));
    if (remote && value !== '' && !options.some((option) => option.id === value)) {
      const known = picked && picked.id === value ? picked : valueLabel ? { id: value, name: valueLabel } : null;
      if (known) list.unshift({ ...known, id: toKey(known.id) });
    }
    return list;
  }, [options, remote, value, picked, valueLabel]);

  const selectedName = items.find((item) => item.id === toKey(value))?.name;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchRef = useRef(onSearch);
  searchRef.current = onSearch;
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const typed = (text: string) => {
    if (!remote) return;
    // RAC зовёт onInputChange и когда сам подставляет название выбранного: это не запрос.
    if (selectedName !== undefined && text === selectedName) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => searchRef.current?.(text), searchDelay);
  };

  return (
    <ComboBox
      className={fieldClass(size, className)}
      selectedKey={value === '' ? null : toKey(value)}
      onSelectionChange={(key) => {
        if (key === null) return;
        const next = fromKey(key) as V | '';
        const choice = items.find((item) => item.id === key);
        if (choice) setPicked({ id: next as V, name: choice.name, description: choice.description });
        onChange(next);
      }}
      {...(remote ? { items, onInputChange: typed } : { defaultItems: items })}
      menuTrigger="focus"
      allowsEmptyCollection
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isRequired={isRequired}
      validationBehavior="aria"
      aria-label={label ? undefined : ariaLabel}
    >
      <FieldLabel label={label} ariaLabel={ariaLabel} explain={explain} isRequired={isRequired} />
      <Group className="ac-trigger" isDisabled={isDisabled}>
        <Search size={15} />
        <Input placeholder={placeholder ?? t(FM.searchPlaceholder)} />
        <Button aria-label={t(FM.openList)}>
          <ChevronDown size={15} />
        </Button>
      </Group>
      <FieldFoot description={description} errorMessage={errorMessage} isInvalid={isInvalid} />
      <Popover className="ac-popover fm-popover">
        <ListBox className="ac-list" renderEmptyState={() => <div className="ac-empty">{isLoading ? t(FM.loading) : (emptyText ?? t(FM.nothingFound))}</div>}>
          {(item: Choice & { id: string }) => <Option item={item} plain />}
        </ListBox>
      </Popover>
    </ComboBox>
  );
}
